import React from "react";
import type { SimoChat } from "../views/useSimoChat";
import { MODEL_OPTIONS } from "../views/useSimoChat";
import { voiceSelectionStatus } from "../voice/voiceSelectionStatus";
import { accentClass } from "./orbAccent";
import { OWNER_NAVIGATION, OWNER_WORKSPACE_LABELS, type OwnerNavId, type OwnerSurface } from "./ownerNavigation";

export { voiceSelectionStatus } from "../voice/voiceSelectionStatus";

export type Surface = OwnerSurface;

const ORB_LABEL: Record<string, string> = {
  idle: "Bereit", wake: "Hört zu", thinking: "Denkt", speaking: "Spricht",
  acting: "Arbeitet", blocked: "Wartet", confirming: "Wartet auf Go", error: "Fehler",
};

export function TopBar({
  chat, surface, setSurface, onOpenSettings, activeNav, onNavigate, sessionActive = true, onLogout, loggingOut = false, openDecisions = 0, notificationCount = 0, systemStatus = "bereit", openGates, onOpenGates, onOpenNotifications,
}: {
  chat: SimoChat;
  surface: Surface;
  setSurface: (s: Surface) => void;
  onOpenSettings: () => void;
  activeNav?: OwnerNavId;
  onNavigate?: (id: OwnerNavId) => void;
  sessionActive?: boolean;
  onLogout?: () => void;
  loggingOut?: boolean;
  openDecisions?: number;
  notificationCount?: number;
  /** OWNEXP F-04: offene Human Gates (kanonisch aus Summary) + Ziel für Klick. */
  openGates?: number | null;
  onOpenGates?: () => void;
  onOpenNotifications?: () => void;
  systemStatus?: string;
}): JSX.Element {
  const { voice, voiceMode, setVoiceMode, model, setModel, roleId, setRoleId, roles, ttsVoice, setTtsVoice, voiceOptions, elevenConfigured, wakewordGate, wakewordRequested, setWakewordRequested, canSelectMic, stopAll, testTtsVoice, ttsTestStatus, orb } = chat;
  const selectedVoice = voiceOptions.find((option) => option.id === ttsVoice);
  const voiceStatus = voiceSelectionStatus({ selected: selectedVoice, elevenConfigured });
  const showVoiceSetupAction = voiceStatus.action !== "Test sprechen" && voiceStatus.action !== "Fallback aktiv";
  const currentWorkspace = OWNER_WORKSPACE_LABELS[surface] ?? "SIMO";
  const [workspaceOpen, setWorkspaceOpen] = React.useState(false);
  React.useEffect(() => { setWorkspaceOpen(false); }, [surface]);
  void setSurface;

  // SIMO-OWNER-SESSION-UI-REACHABILITY-1: die drei Kopfzeilen-Menues sind
  // native <details>-Elemente. Nativ weiss keines vom anderen — dadurch standen
  // Workspace-, Control- und Owner-Menue gleichzeitig offen und haben den
  // Inhalt darunter verdeckt. Ausserdem schliesst ein natives <details> weder
  // bei Escape noch bei einem Klick daneben. Beides wird hier zentral geregelt,
  // damit sich die Kopfzeile wie ein fertiges Produkt verhaelt.
  const headerRef = React.useRef<HTMLElement | null>(null);
  React.useEffect(() => {
    const header = headerRef.current;
    if (!header) return;
    const menus = (): HTMLDetailsElement[] =>
      Array.from(header.querySelectorAll<HTMLDetailsElement>("details[data-simo-menu]"));
    const closeAll = (except?: HTMLDetailsElement): void => {
      for (const menu of menus()) {
        if (menu !== except && menu.open) menu.open = false;
      }
    };
    // Genau ein Menue offen: oeffnet eines, schliessen die anderen.
    const onToggle = (event: Event): void => {
      const target = event.target as HTMLDetailsElement | null;
      if (target?.open) closeAll(target);
    };
    const onPointerDown = (event: PointerEvent): void => {
      const node = event.target as Node | null;
      if (node && header.contains(node)) return;
      closeAll();
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      const open = menus().find((menu) => menu.open);
      if (!open) return;
      open.open = false;
      // Fokus zurueck auf den Ausloeser, damit Tastaturbedienung nicht ins
      // Leere laeuft.
      open.querySelector<HTMLElement>("summary")?.focus();
    };
    header.addEventListener("toggle", onToggle, true);
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      header.removeEventListener("toggle", onToggle, true);
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  return (
    <header ref={headerRef} className="relative z-50 border-b border-jarvis-border bg-jarvis-surface/88 backdrop-blur-md shadow-[0_10px_28px_rgba(0,0,0,0.22)]">
      <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2.5 md:px-5">
        <div className="flex min-w-0 items-center gap-2.5 mr-1">
          <div className="relative grid h-9 w-9 place-items-center rounded-lg border border-jarvis-accent/35 bg-jarvis-bg/80 shadow-[0_0_24px_rgba(212,164,73,0.16)]">
            <img src="/brand/simo-icon-64.png" alt="" className="h-7 w-7" />
            <span className={`status-dot ${accentClass(orb)} absolute -right-0.5 -top-0.5`} style={{ width: 9, height: 9 }} />
          </div>
          <div className="min-w-0 leading-tight">
            <div className="text-sm font-semibold tracking-wide text-jarvis-text">SIMO</div>
            <div className="text-[9px] uppercase tracking-[0.22em] text-jarvis-muted">
              Agent OS · {ORB_LABEL[orb] ?? orb}
            </div>
          </div>
        </div>

        <details className="relative min-w-0" data-simo-menu data-simo-workspace-switcher open={workspaceOpen} onToggle={(event) => setWorkspaceOpen(event.currentTarget.open)}>
          <summary className="topbar-control inline-flex min-h-11 max-w-[230px] cursor-pointer list-none items-center gap-2 px-3 text-left marker:hidden" aria-label="Aktueller Workspace und Navigation öffnen">
            <span className="min-w-0">
              <span className="block text-[9px] uppercase tracking-[0.18em] text-jarvis-muted">Workspace</span>
              <span className="block truncate text-xs font-semibold text-jarvis-text">{currentWorkspace}</span>
            </span>
            <span className="text-jarvis-muted">▾</span>
          </summary>
          <nav aria-label="SIMO Agent OS Navigation" className="absolute left-0 z-50 mt-2 grid w-[min(88vw,320px)] gap-1 rounded-xl border border-jarvis-border bg-jarvis-surface/98 p-2 shadow-[0_22px_60px_rgba(0,0,0,0.42)]">
            {OWNER_NAVIGATION.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={(event) => {
                  const details = event.currentTarget.closest("details") as HTMLDetailsElement | null;
                  setWorkspaceOpen(false);
                  onNavigate?.(n.id);
                  window.setTimeout(() => {
                    setWorkspaceOpen(false);
                    details?.removeAttribute("open");
                  }, 0);
                }}
                aria-current={activeNav === n.id ? "page" : undefined}
                className={`min-h-11 rounded-lg px-3 text-left text-sm transition-colors ${
                  activeNav === n.id
                    ? "border border-jarvis-accent/50 bg-jarvis-accent/20 text-jarvis-text shadow-[0_0_18px_rgba(212,164,73,0.12)]"
                    : "border border-transparent text-jarvis-muted hover:bg-jarvis-bg/60 hover:text-jarvis-text"
                }`}
              >
                {n.label}
              </button>
            ))}
          </nav>
        </details>

        <div className="order-2 ml-auto flex min-w-0 flex-wrap items-center justify-end gap-2 md:order-none">
          <div className="rounded-lg border border-emerald-300/20 bg-emerald-300/10 px-3 py-2 leading-tight" aria-label="Globaler Systemstatus">
            <p className="text-[9px] uppercase tracking-[0.18em] text-emerald-200/70">Status</p>
            <p className="text-xs font-semibold text-emerald-100">{systemStatus}</p>
          </div>
          {/* OWNEXP F-04 (IA-J3): Human-Gate-Indikator in der Topbar — immer
              sichtbar, klickbar zum Gate-Inbox; nie nur Farbe (Zahl + Label). */}
          {onOpenGates ? (
            <button
              type="button"
              className={`min-h-11 rounded-lg border px-3 py-2 text-left leading-tight transition ${
                (openGates ?? 0) > 0 ? "border-amber-400/60 bg-amber-400/10" : "border-white/10 bg-black/25"
              } focus:outline-none focus-visible:ring-2 focus-visible:ring-jarvis-accent/70`}
              onClick={onOpenGates}
              aria-label={`Offene Human Gates: ${openGates === null || openGates === undefined ? "Zähler nicht abrufbar" : openGates}. Bereich öffnen.`}
              data-testid="topbar-gates-chip"
            >
              <p className="text-[9px] uppercase tracking-[0.18em] text-jarvis-muted">Gates</p>
              <p className={`text-xs font-semibold ${(openGates ?? 0) > 0 ? "text-amber-200" : "text-jarvis-text"}`}>
                {openGates === null || openGates === undefined ? "—" : openGates}
              </p>
            </button>
          ) : null}
          <div className="rounded-lg border border-white/10 bg-black/25 px-3 py-2 leading-tight" aria-label="Offene Lauf-Freigaben (Approval-Inbox, nicht Human Gates)">
            {/* OPNAV1 Truth-Fixture: Lauf-Freigaben ≠ Human-Gate-Authority —
                getrennt gelabelt statt kosmetischem Mapping. Owner-Gates stehen
                kanonisch in der Control Plane (Attention/Overview). */}
            <p className="text-[9px] uppercase tracking-[0.18em] text-jarvis-muted">Freigaben (Läufe)</p>
            <p className="text-xs font-semibold text-jarvis-text">{openDecisions}</p>
          </div>
          {/* OWNEXP F-05: Hinweise-Chip führt zur Benachrichtigungs-Liste
              (Attention-Bereich), kein toter Zähler mehr. */}
          {onOpenNotifications ? (
            <button
              type="button"
              className="min-h-11 rounded-lg border border-white/10 bg-black/25 px-3 py-2 text-left leading-tight transition hover:border-jarvis-accent/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-jarvis-accent/70"
              onClick={onOpenNotifications}
              aria-label={`Hinweise: ${notificationCount}. Benachrichtigungen öffnen.`}
              data-testid="topbar-notifications-chip"
            >
              <p className="text-[9px] uppercase tracking-[0.18em] text-jarvis-muted">Hinweise</p>
              <p className="text-xs font-semibold text-jarvis-text">{notificationCount}</p>
            </button>
          ) : (
            <div className="rounded-lg border border-white/10 bg-black/25 px-3 py-2 leading-tight" aria-label="Benachrichtigungen">
              <p className="text-[9px] uppercase tracking-[0.18em] text-jarvis-muted">Hinweise</p>
              <p className="text-xs font-semibold text-jarvis-text">{notificationCount}</p>
            </div>
          )}

          <button
            className={`topbar-control grid h-11 w-11 place-items-center rounded-full px-0 ${
              voiceMode === "server" ? "border-jarvis-accent text-jarvis-accent" : "text-jarvis-muted"
            }`}
            onClick={() => {
              if (voiceMode === "server") { stopAll(); setVoiceMode("off"); }
              else setVoiceMode("server");
            }}
            title="Voice-Orb umschalten"
            aria-label={voiceMode === "server" ? "Voice-Orb ist aktiv" : "Voice-Orb ist aus"}
          >
            <span className={`status-dot ${voiceMode === "server" ? "simo-accent-speaking" : "simo-accent-neutral"}`} />
          </button>

          <details className="relative" data-simo-menu data-simo-control-popover>
            <summary className="topbar-control inline-flex min-h-11 cursor-pointer list-none items-center gap-2 text-jarvis-text marker:hidden">
              <span className="hidden sm:inline">SIMO-Control</span>
              <span className="sm:hidden">Control</span>
              <span className="text-jarvis-muted">▾</span>
            </summary>
            <div className="simo-keyboard-safe-editor absolute right-0 z-50 mt-2 grid w-[min(92vw,650px)] min-w-0 gap-2 rounded-xl border border-jarvis-border bg-jarvis-surface/98 p-3 shadow-[0_22px_60px_rgba(0,0,0,0.42)] sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.25fr)_auto_auto]" aria-label="SIMO-Control: Modell, Rolle, Voice und Mic">
              <select
                className="topbar-control simo-keyboard-safe-field min-h-11 w-full min-w-0 text-base sm:text-xs"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                title="Routing oder Modell für nächste Anfrage"
              >
                {MODEL_OPTIONS.map((m) => (
                  <option key={m.id} value={m.id}>{m.label}</option>
                ))}
              </select>

              <select
                className="topbar-control simo-keyboard-safe-field min-h-11 w-full min-w-0 text-base sm:text-xs"
                value={roleId}
                onChange={(e) => setRoleId(e.target.value)}
                title="Agentenrolle für nächste Anfrage"
              >
                <option value="">Rolle: Auto</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}{role.permissionFloor !== "none" ? ` · ${role.permissionFloor}` : ""}
                  </option>
                ))}
              </select>

              <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] gap-1">
                <select
                  className="topbar-control simo-keyboard-safe-field min-h-11 w-full min-w-0 text-base sm:text-xs"
                  value={ttsVoice}
                  onChange={(e) => setTtsVoice(e.target.value as typeof ttsVoice)}
                  title={voiceStatus.detail}
                  aria-label="TTS-Stimme"
                >
                  {voiceOptions.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.label}{v.fallback ? " — Fallback" : ""}{v.available === false ? ` — ${v.actionLabel ?? "nicht verfügbar"}` : ""}
                    </option>
                  ))}
                </select>

                <button
                  className="topbar-control inline-flex items-center justify-center"
                  onClick={() => void testTtsVoice()}
                  disabled={voice.speaking}
                  title={voiceStatus.detail}
                >
                  {voiceStatus.testLabel}
                </button>
                <span className="col-span-2 min-w-0 truncate rounded-md border border-white/10 bg-black/25 px-2 py-1 text-[10px] text-jarvis-muted" title={`${voiceStatus.label} · ${voiceStatus.detail} · ${voiceStatus.action} · ${voiceStatus.playback}`}>
                  <strong className="font-semibold text-jarvis-text">{voiceStatus.label}</strong> · {voiceStatus.action} · {voiceStatus.playback}
                </span>
                <span className="col-span-2 min-w-0 rounded-md border border-white/10 bg-black/25 px-2 py-1 text-[10px] text-jarvis-muted" title={voiceStatus.reason}>
                  Gewählt: <strong className="font-semibold text-jarvis-text">{voiceStatus.selectedLabel}</strong> · Test spielt:{" "}
                  <strong className="font-semibold text-jarvis-text">{voiceStatus.playbackLabel}</strong>
                </span>
                <span className="col-span-2 min-w-0 rounded-md border border-white/10 bg-black/25 px-2 py-1 text-[10px] text-jarvis-muted" data-state={ttsTestStatus.state} title={ttsTestStatus.detail}>
                  <strong className="font-semibold text-jarvis-text">{ttsTestStatus.label}</strong> · {ttsTestStatus.detail}
                </span>
                {showVoiceSetupAction && (
                  <button
                    className="topbar-control col-span-2 inline-flex min-h-[30px] items-center justify-center text-[10px] text-jarvis-text"
                    onClick={onOpenSettings}
                    title={`${voiceStatus.action}: ${voiceStatus.reason}`}
                    aria-label={`Voice Setup öffnen: ${voiceStatus.action}`}
                  >
                    {voiceStatus.action}
                  </button>
                )}
              </div>

              <select
                className="topbar-control simo-keyboard-safe-field min-h-11 w-full min-w-0 text-base sm:text-xs"
                value={canSelectMic ? voice.selectedDeviceId : ""}
                onChange={(e) => voice.setSelectedDeviceId(e.target.value)}
                onFocus={() => void voice.refreshDevices()}
                disabled={!canSelectMic}
                title="Mikrofon für Push-to-Talk"
              >
                {!canSelectMic ? (
                  <option value="">Mic prüfen…</option>
                ) : voice.devices.length === 0 ? (
                  <option value="">Kein Mikrofon</option>
                ) : (
                  voice.devices.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label}</option>)
                )}
              </select>

              <button
                className={`topbar-control inline-flex items-center justify-center gap-1.5 ${
                  wakewordGate.active ? "border-sky-400 text-sky-300" : "text-jarvis-muted"
                }`}
                onClick={() => {
                  if (wakewordGate.canRequest) setWakewordRequested(!wakewordRequested);
                }}
                disabled={!wakewordGate.canRequest}
                title={`Wakeword: ${wakewordGate.reason}`}
              >
                <span className={`status-dot ${wakewordGate.active ? "simo-accent-wake" : "simo-accent-neutral"}`} />
                {wakewordGate.label}
              </button>

              <button className="topbar-control text-jarvis-muted hover:text-jarvis-text" onClick={stopAll} title="Stop">
                Stop
              </button>
            </div>
          </details>

          <details className="relative" data-simo-menu data-simo-owner-profile>
            <summary
              className="topbar-control inline-flex min-h-11 cursor-pointer list-none items-center gap-2 text-jarvis-text marker:hidden"
              title="Owner-Profil"
              aria-label="Owner-Profil öffnen"
            >
              Owner
              <span className="text-jarvis-muted">▾</span>
            </summary>
            <div className="absolute right-0 z-50 mt-2 grid w-[min(86vw,260px)] gap-2 rounded-xl border border-jarvis-border bg-jarvis-surface/98 p-3 shadow-[0_22px_60px_rgba(0,0,0,0.42)]" aria-label="Owner-Profil und Einstellungen">
              <button
                className="topbar-control inline-flex min-h-11 items-center justify-center text-jarvis-muted hover:text-jarvis-text"
                onClick={() => onNavigate?.("brain")}
                disabled={!sessionActive}
                title={sessionActive ? "Brain öffnen" : "Owner-Session erforderlich"}
              >
                Brain
              </button>

              <button
                className="topbar-control inline-flex min-h-11 items-center justify-center"
                onClick={onOpenSettings}
                title="Owner-Profil und Einstellungen"
                aria-label="Owner-Profil und Einstellungen öffnen"
              >
                Einstellungen
              </button>

              {sessionActive && onLogout ? (
                <button
                  className="topbar-control inline-flex min-h-11 items-center justify-center text-jarvis-muted hover:text-jarvis-text disabled:opacity-50"
                  onClick={onLogout}
                  disabled={loggingOut}
                  title="Owner-Session abmelden"
                  aria-label="Owner-Session abmelden"
                >
                  {loggingOut ? "Abmelden..." : "Abmelden"}
                </button>
              ) : null}
            </div>
          </details>
        </div>
      </div>
    </header>
  );
}
